<script setup>
import {
    computed, onBeforeMount,
    ref,
} from 'vue';
import {
    storeToRefs,
} from 'pinia';

import MinitaskDesiredJobTravelReadiness from './MinitaskDesiredJobTravelReadiness';
import MinitaskDesiredJobWorkFromHome from './MinitaskDesiredJobWorkFromHome';
import MinitaskAddress from './MinitaskAddress';
import MinitaskBirthdate from './MinitaskBirthdate';
import MinitaskDesiredJobEmploymentType from './MinitaskDesiredJobEmploymentType';
import MinitaskDesiredJobJobfield from './MinitaskDesiredJobJobfield';
import MinitaskImage from './MinitaskImage';
import MinitaskLanguage from './MinitaskLanguage';
import MinitaskJobTitle from './MinitaskJobTitle';
import MinitaskJobField from './MinitaskJobField';
import MinitaskSoftSkills from './MinitaskSoftSkills';
import MinitaskSuccess from './MinitaskSuccess';
import MinitaskTelephone from './MinitaskTelephone';
import MinitaskVisibility from './MinitaskVisibility';
import MinitaskWork from './MinitaskWork';
import {
    useSoftSkills,
} from '../../../composables/resource-soft-skills';
import {
    useMinitaskStore,
} from '../../../stores/minitasks-store';
import {
    useProfileStore,
} from '../../../stores/profile-store';
import {
    useAuth,
} from '../../../stores/user-store';

defineProps({
    gaPrefix: {
        type: String,
    },
});

const emit = defineEmits(['add']);

const { hasEmptySoftSkillGroups } = useSoftSkills();
const isFetching = ref(true);

const profile = useProfileStore();
const {
    workExperiences,
} = storeToRefs(profile.workExperiences);

const storedWorkExperiences = computed(() => workExperiences.value.filter(x => x.id));

const currentWorkExperienceWithoutTitle = computed(() => {
    const matchCounter = storedWorkExperiences.value.filter(x => !x.title).length;
    let workExperienceWithoutTitle;

    for (let i = 0; i < matchCounter; i += 1) {
        workExperienceWithoutTitle = storedWorkExperiences.value.filter(x => !x.title)[i] ?? {};

        if (workExperienceWithoutTitle?.category.id === 1) return workExperienceWithoutTitle;
    }

    return null;
});

const currentWorkExperienceWithoutField = computed(() => {
    const matchCounter = storedWorkExperiences.value.filter(x => !x.jobfield.id).length;
    let workExperienceWithoutField;

    for (let i = 0; i < matchCounter; i += 1) {
        workExperienceWithoutField = storedWorkExperiences.value.filter(x => !x.jobfield.id)[i] ?? {};

        if (workExperienceWithoutField?.category.id === 1) return workExperienceWithoutField;
    }

    return null;
});

const minitasks = useMinitaskStore();

const auth = useAuth();
onBeforeMount(async () => {
    minitasks.fetchMinitasks({ uuid: auth.uuid, callback: () => { isFetching.value = false; } });
});
</script>

<template>
    <div
        v-if="profile.exists"
    >
        <MinitaskDesiredJobTravelReadiness
            v-if="minitasks.desiredJobTravelReadinessDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Travel Readiness' : undefined
            }"
            data-qa="travel readiness minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('desiredJobTravelReadiness')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskDesiredJobWorkFromHome
            v-else-if="minitasks.desiredJobWorkFromHomeDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Homeoffice' : undefined
            }"
            data-qa="work from home minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('desiredJobWorkFromHome')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskImage
            v-else-if="minitasks.imageDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Profile Photo' : undefined
            }"
            data-qa="image minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('image')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskBirthdate
            v-else-if="minitasks.birthdateDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Birthday' : undefined
            }"
            data-qa="birthdate minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('birthdate')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskTelephone
            v-else-if="minitasks.telephoneDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Phone Number' : undefined
            }"
            data-qa="telephone minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('mobile')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskAddress
            v-else-if="minitasks.addressDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Address' : undefined
            }"
            data-qa="address minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('address')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskDesiredJobEmploymentType
            v-else-if="minitasks.desiredJobEmploymentTypeDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Desired Job Employment Type' : undefined
            }"
            data-qa="desired job employment minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('desiredJobEmploymentType')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskDesiredJobJobfield
            v-else-if="minitasks.desiredJobJobfieldDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Desired Job Field' : undefined
            }"
            data-qa="desired job jobfield minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('desiredJobJobfield')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskSoftSkills
            v-else-if="hasEmptySoftSkillGroups && minitasks.softSkillsSkippedStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Soft Skills' : undefined
            }"
            data-qa="soft skills minitask"
            :ga-prefix="gaPrefix"
            @add="emit('add', $event)"
            @skipped="minitasks.setSkipped('softSkills')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskLanguage
            v-else-if="minitasks.languageDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Language' : undefined
            }"
            data-qa="language minitask"
            :ga-prefix="gaPrefix"
            @add="emit('add', $event)"
            @skipped="minitasks.setSkipped('language')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskWork
            v-else-if="minitasks.workDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Experience' : undefined
            }"
            data-qa="work minitask"
            :ga-prefix="gaPrefix"
            @add="emit('add', $event)"
            @skipped="minitasks.setSkipped('work')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskJobTitle
            v-else-if="currentWorkExperienceWithoutTitle && minitasks.jobTitleDisplayStatus && !isFetching"
            :id="currentWorkExperienceWithoutTitle.id"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Employer Position' : undefined
            }"
            data-qa="job title minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('jobTitle')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskJobField
            v-else-if="currentWorkExperienceWithoutField && minitasks.jobFieldDisplayStatus && !isFetching"
            :id="currentWorkExperienceWithoutField.id"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Employer Field' : undefined
            }"
            data-qa="job field minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('jobField')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskVisibility
            v-else-if="minitasks.visibilityDisplayStatus && !isFetching"
            v-track-visibility="{
                element: gaPrefix ? `${gaPrefix}: Minitask` : undefined,
                elementDetail: gaPrefix ? 'Activate Profile' : undefined
            }"
            data-qa="visibility minitask"
            :ga-prefix="gaPrefix"
            @skipped="minitasks.setSkipped('visibility')"
            @updated="minitasks.setMinitaskSuccessStatus"
        />
        <MinitaskSuccess
            v-else-if="minitasks.successDisplayStatus"
            data-qa="success minitask"
            @hide="minitasks.setMinitaskSuccessHide"
        />
    </div>
</template>
